import { useEffect } from 'preact/hooks'
import { route } from 'preact-router'
import { useAuth } from '../hooks/useAuth'
import { AuthCredentialsSchema } from '../types/schemas'
import { apiService } from '../services/api'
import { signal } from '@preact/signals'
import { FormField } from '../components/FormField'
import { ErrorAlert } from '../components/ErrorAlert'

const username = signal(import.meta.env.VITE_TVMAZE_USERNAME || '')
const apiKey = signal(import.meta.env.VITE_TVMAZE_API_KEY || '')
const error = signal('')

export function Login() {
  const { login, isLoading, error: authError } = useAuth()

  useEffect(() => {
    // If already authenticated, redirect to shows
    if (apiService.getCredentials()) {
      route('/shows')
    }
  }, [])

  const handleSubmit = (e: Event) => {
    e.preventDefault()
    error.value = ''

    try {
      const credentials = AuthCredentialsSchema.parse({
        username: username.value,
        apiKey: apiKey.value,
      })
      login(credentials, {
        onSuccess: () => {
          route('/shows')
        },
        onError: err => {
          error.value = err.message || 'Invalid credentials'
        },
      })
    } catch {
      error.value = 'Please enter both username and API key'
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-slate-800 rounded-lg shadow-xl p-8">
          <h1 className="text-3xl font-bold text-white mb-2">TVMaze Tracker</h1>
          <p className="text-slate-400 mb-8">Sign in with your TVMaze credentials</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <FormField
              id="username"
              label="Username"
              type="text"
              value={username}
              placeholder="Your TVMaze username"
              disabled={isLoading}
            />

            <FormField
              id="apiKey"
              label="API Key"
              type="password"
              value={apiKey}
              placeholder="Your TVMaze API key"
              disabled={isLoading}
              helperText="Get your API key from"
              helperLink={{
                text: 'TVMaze Dashboard',
                href: 'https://www.tvmaze.com/dashboard',
              }}
            />

            {(error.value || authError) && (
              <ErrorAlert message={error.value || authError?.message || 'An error occurred'} />
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-400">
              Need a premium account?{' '}
              <a
                href="https://www.tvmaze.com/premium"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                Get TVMaze Premium
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
